/* Petrol top */
.hero {
  --journey-scrim-x: -28px;
  --journey-scrim-y: -16px;
  background: linear-gradient(135deg, var(--color-primary-light), var(--color-primary) 55%, var(--color-primary-deep));
  border-radius: 16px 16px 0 0;
  color: var(--color-text-on-primary);
  isolation: isolate;
  overflow: hidden;
  padding: 24px 32px 28px;
  position: relative;
}

/* The text lies over the band and the soft field. */
.hero > :not([aria-hidden]) {
  position: relative;
  z-index: 1;
}

.back,
.factLink {
  color: var(--color-text-on-primary);
  font-size: var(--font-size-small);
  opacity: 0.85;
}

.back {
  text-decoration: none;
}

.back:hover,
.back:focus-visible,
.factLink:hover {
  opacity: 1;
  text-decoration: underline;
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.badge,
.statusBadge {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  font-size: var(--font-size-small);
  padding: 2px 12px;
}

.statusBadge {
  background: var(--color-cta);
  border-color: var(--color-cta);
  color: var(--color-primary-deep);
  font-weight: 600;
}

.titleRow {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin: 8px 0;
  min-width: 0;
}

.title {
  color: var(--color-text-on-primary);
  font-family: var(--font-family-heading);
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.facts {
  color: var(--color-text-on-primary-soft);
  display: flex;
  flex-wrap: wrap;
  font-size: var(--font-size-small);
  gap: 4px 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.primary,
.secondary {
  border-radius: 999px;
  cursor: pointer;
  font-family: var(--font-family-base);
  font-size: var(--font-size-small);
  padding: 10px 20px;
  text-decoration: none;
}

.primary {
  background: var(--color-cta);
  border: 1px solid var(--color-cta);
  color: var(--color-primary-deep);
  font-weight: 400;
}

.secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: var(--color-text-on-primary);
}

.secondary[aria-pressed="true"] {
  background: rgba(255, 255, 255, 0.15);
}

.primary:focus-visible,
.secondary:focus-visible,
.back:focus-visible,
.factLink:focus-visible {
  outline: 3px solid var(--color-accent);
  outline-offset: 2px;
}

/* Phases on the journey line, full width under the top */
.phases {
  background: var(--color-primary-deep);
  border-radius: 0 0 16px 16px;
  color: var(--color-text-on-primary);
  padding: 8px 32px 24px;
}

.journey {
  display: block;
  height: 16px;
  width: 100%;
}

.phaseList {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}

.phase {
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  gap: 2px;
  min-width: 0;
}

.phase:nth-child(2) {
  text-align: center;
}

.phase:nth-child(3) {
  text-align: right;
}

.dot {
  display: none;
}

.upcoming {
  opacity: 0.7;
}

.active strong {
  color: var(--color-cta);
}

.phaseDates {
  opacity: 0.85;
}

.phaseText {
  overflow-wrap: anywhere;
}

.details {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.details .card {
  margin-bottom: 0;
}

.details {
  margin-bottom: 24px;
}

.card,
.sideCard {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: var(--shadow-close);
  margin-bottom: 24px;
  min-width: 0;
  padding: 20px 24px;
}

.dark {
  background: linear-gradient(160deg, var(--color-primary-light), var(--color-primary-deep));
  color: var(--color-text-on-primary);
}

.cardHeading,
.sideHeading {
  font-family: var(--font-family-heading);
  font-size: 1.5rem;
  margin: 0 0 12px;
  text-align: left;
}

.text {
  font-size: var(--font-size-small);
  margin: 0 0 12px;
}

.preWrap {
  margin: 0 0 12px;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.coach {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.coach img {
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  object-fit: cover;
}

.button {
  border: 1px solid var(--color-primary);
  border-radius: 999px;
  color: var(--color-primary);
  display: inline-block;
  font-size: var(--font-size-small);
  padding: 8px 16px;
  text-decoration: none;
}

.button:hover {
  background: var(--color-tertiary-background);
}

.button:focus-visible {
  outline: 3px solid var(--color-info-border);
  outline-offset: 2px;
}

/* Latest surveys */
.tableScroll {
  overflow-x: auto;
}

.table {
  border-collapse: collapse;
  font-size: var(--font-size-small);
  width: 100%;
}

.table th,
.table td {
  border-bottom: 1px solid var(--color-muted);
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}

.table thead th {
  font-weight: 600;
  white-space: nowrap;
}

.tag {
  background: var(--color-tertiary-background);
  border-radius: 999px;
  display: inline-block;
  font-weight: normal;
  margin-left: 8px;
  padding: 0 8px;
}

.scoreCell {
  align-items: center;
  display: flex;
  gap: 6px;
  white-space: nowrap;
}

.diff {
  font-weight: 600;
}

.diffPositive {
  color: var(--color-success, #2f7d4f);
}

.diffNegative {
  color: var(--color-error, #b5603f);
}

.diffNeutral {
  opacity: 0.7;
}

/* One column under 1000px, the side panel after the main content. */
.main {
  container-type: inline-size;
}

/* Kortene trenger minst 300 px hver: 3 × 300 px + 2 × 24 px mellomrom. */
@container (width < 948px) {
  .details {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 599px) {
  .hero {
    padding: 20px 16px 24px;
  }

  .phases {
    padding: 8px 16px 20px;
  }

  .phaseList {
    grid-template-columns: minmax(0, 1fr);
  }

  .phase:nth-child(n) {
    text-align: left;
  }

  .card,
  .sideCard {
    padding: 16px;
  }
}
